import React from 'react';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ReportOutlinedIcon from '@mui/icons-material/ReportOutlined';
import LiveHelpOutlinedIcon from '@mui/icons-material/LiveHelpOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { ConversationCategory } from '../types/conversation';

/**
 * Pieces of the live insight card (`ConversationEntry`) that Knowledge Search
 * shows too, so an objection or FAQ found there looks like the card the rep
 * gets during the call.
 */

const tint = (token: string, percent: number) => `color-mix(in srgb, var(${token}) ${percent}%, transparent)`;

// Each category's tint, text colour token and macOS CategoryBadge icon.
const getCategoryStyle = (category: string): { className: string; fg: string; Icon: typeof DescriptionOutlinedIcon } => {
  switch (category) {
    case ConversationCategory.FAQ:
      return { className: 'bg-success-tint text-success', fg: '--success', Icon: HelpOutlineIcon };
    case ConversationCategory.OBJECTION:
      return { className: 'bg-error-tint text-error', fg: '--error', Icon: ReportOutlinedIcon };
    case ConversationCategory.HESITATION:
      return { className: 'bg-warning-tint text-warning', fg: '--warning', Icon: LiveHelpOutlinedIcon };
    case ConversationCategory.NEW_INQUIRY:
      return { className: 'bg-info-tint text-info', fg: '--info', Icon: LightbulbOutlinedIcon };
    case ConversationCategory.OTHER:
    default:
      return { className: 'bg-surface-subtle text-ink-muted', fg: '--ink-muted', Icon: DescriptionOutlinedIcon };
  }
};

// "Category: Objection", as the macOS CategoryBadge and WPF chip read.
const getCategoryText = (category: string) => (
  `Category: ${category === ConversationCategory.NEW_INQUIRY ? 'New Objection/FAQ' : category}`
);

/** The category chip: its tint and text, a 1px border in the text colour at 30% and the macOS CategoryBadge icon. */
export const CategoryChip: React.FC<{ category: string }> = ({ category }) => {
  const categoryStyle = getCategoryStyle(category);
  const CategoryIcon = categoryStyle.Icon;
  return (
    <div
      className={`inline-flex items-center gap-1 rounded-control border px-2.5 py-1 text-[10px] font-semibold ${categoryStyle.className}`}
      style={{ borderColor: tint(categoryStyle.fg, 30) }}
    >
      <CategoryIcon sx={{ fontSize: 10 }} />
      {getCategoryText(category)}
    </div>
  );
};

/** An answer's text: a line starting with a bullet mark is a list item, any other non-empty line a paragraph. */
export const AnswerText: React.FC<{ answer: string }> = ({ answer }) => {
  const formattedLines = answer.split('\n').map((line, index) => {
    // Handle bullet points
    if (line.trim().startsWith('•') || line.trim().startsWith('-') || line.trim().startsWith('*')) {
      return (
        <li key={index} className="ml-3 mb-0.5 text-sm">
          {line.trim().substring(1).trim()}
        </li>
      );
    }
    // Handle regular text
    if (line.trim()) {
      return (
        <p key={index} className="mb-1 text-sm">
          {line.trim()}
        </p>
      );
    }
    return null;
  }).filter(Boolean);

  return <div>{formattedLines}</div>;
};
